/*******************************
 *                             *
 *          TYPE DEFS          *
 *                             *
 *******************************/
export interface IQuery {
  advertisements?: Array<IAdvertisement | null>;
  collection?: ICollection;
  collections?: Array<ICollection | null>;
  collectionPanels?: Array<ICollectionPanel | null>;
  editorsPickSale?: ISale;
  favorites?: Array<string | null>;
  sales?: Array<ISale | null>;
  sale?: ISale;
  territoryConfiguration?: ITerritoryConfiguration;
  recommendations?: Array<ISale | null>;
  saleSearch?: ISearchResults;
  recentlyViewedSales?: Array<ISale | null>;
  upcomingSales?: IUpcomingSalesResults;
}

export enum IPageName {
  currentSales = 'currentSales',
  collections = 'collections',
  bookingConfirmation = 'bookingConfirmation',
  bookingDetailsPage = 'bookingDetails',
  bookingAmendmentsPage = 'bookingAmendment',
  confirmPayment = 'confirmPayment',
  searchResults = 'searchResults',
  offers = 'offers',
  salePage = 'salePage',
  cancellationPage = 'cancellation',
  bookingsPage = 'bookings',
  travelBirdSalePage = 'travelBirdSalePage',
  instantAccess = 'instantAccess',
}

export interface IAdvertisement {
  /**
   * Concatenation of page and info about place where ad should be placed
   */
  placementKey?: string;

  /**
   * Code snippet to insert
   */
  script?: string;
}

export enum ICollectionPanelType {
  curatedCollections = 'curatedCollections',
  topDestinations = 'topDestinations',
  tripTypes = 'tripTypes',
}

export interface ICollectionPanel {
  /**
   * Absolute URL for collection cover image
   */
  imageUrl?: string;

  /**
   * Absolute URL for collection cover image, with a template to replace with actual height and width
   */
  imageUrlWithSize?: string;

  /**
   * URL for page on site the collection panel links to
   */
  url?: string;

  /**
   * Localised display text for the collection
   */
  text?: string;

  /**
   * Integer representing the display oder of the collection in the panel
   */
  position?: number;
}

export enum RecommendationSource {
  athena_disco = 'athena_disco',
  athena_i2i = 'athena_i2i',
  athena_shot = 'athena_shot',
  jetlore = 'jetlore',
}

export enum SearchSource {
  v4 = 'v4',
  kingfisher = 'kingfisher',
}

export interface ISale {
  /**
   * ID of the sale
   */
  id: string;

  /**
   * Sale type
   */
  type?: ISaleType;

  /**
   * Dates information
   */
  dates?: IDates;

  /**
   * Tags
   */
  tags?: Array<string | null>;

  /**
   * Photos, first one will be main photo
   */
  photos?: Array<IPhoto | null>;

  /**
   * Sale prices
   */
  prices?: ISalePrices;

  /**
   * Attributes
   */
  attributes?: ISaleAttributes;

  /**
   * Urls related
   */
  links?: ISaleLinks;

  /**
   * Sale promotion
   */
  promotion?: ISalePromotion;

  /**
   * Sale labels
   */
  labels?: Array<ISaleLabel | null>;

  /**
   * Transport related information
   */
  travel?: ISaleTravel;

  /**
   * Location
   */
  location?: ILocation;

  /**
   * Editorial content, descriptions of sale and hotel
   */
  editorial?: ISaleEditorial;

  /**
   * Urgency information
   */
  bookingStats?: IBookingStats;

  /**
   * The offers for the sale
   */
  offers?: Array<IOffer | null>;

  /**
   * A specific offer for a sale
   */
  offer?: IOffer;
}

export interface IBooking {
  /**
   * ID of the booking
   */
  id: string;
  transactionId?: string;
  title: string;
  destinationName?: string;
  status?: IBookingStatus;
  saleLocation?: ISaleLocation;
  leadImage: IPhoto;
  bookingDates: IDates;
  amendedDates: IDates;
  duration: number;
  rooms: number;
  occupancy: IOccupancy;
  links?: IBookingLinks;
  hotelDetails?: IHotelDetails;
  cancellationPolicy: ICancellationPolicy;
  saleId: string;
  offerId: string;
}

export enum IBookingStatus {
  UPCOMING = 'UPCOMING',
  PAST = 'PAST',
  CANCELLED = 'CANCELLED',
}

export interface IOccupancy {
  adults: number;
  children: number;
}

export interface IBookingLinks {
  printConfirmationEmail?: string;
  sendConfirmationEmail?: string;
  viewExternalBookingDetails?: string;
  printPdfInvoice?: string;
}

export interface ISaleLocation {
  city: ISaleLocationItem;
  country: ISaleLocationItem;
  continent: ISaleLocationItem;
  division: ISaleLocationItem;
  latitude: number;
  longitude: number;
}

export interface ISaleLocationItem {
  id: number;
  name: string;
}

export interface IHotelDetails {
  address: IAddress;
  customerSupportNumber: string;
  customerSupportEmail: string;
}

export interface IAddress {
  line1: string;
  line2: string;
  line3: string;
  line4: string;
  line5: string;
}

export interface ICancellationPolicy {
  properties: ICancellationPolicyProperty[];
  isCancellable: boolean;
  isRebookable: boolean;
  notCancellableReason: string;
}

export interface ICancellationPolicyProperty {
  key: CancellationPolicyKey;
  value: string;
}

export enum CancellationPolicyKey {
  DEADLINE = 'DEADLINE',
}

/**
 * Hotel type - basic  offer contains of room only. Stock is maintained by SE or fetched from external provider
 * Package - basic offer contains of room and flight. Hotel and flight configuration is done by SE
 * IHP - basic offer contains of room and flight.  Hotel and flight configuration is done by external provider
 * WRD - Web redirect sales - offer contains of either rooms or rooms + flights. User is redirected to partner site in order to see do the booking
 */
export enum ISaleType {
  hotel = 'hotel',
  package = 'package',
  ihp = 'ihp',
  wrd = 'wrd',
}

export interface IDates {
  /**
   * Start date
   */
  start?: string;

  /**
   * End date
   */
  end?: string;
}

export interface IPhoto {
  /**
   * URL
   */
  url?: string;

  /**
   * Image URL with replaceable tokens $width and $height that can be substituted for desired px values to dynamically size the image
   */
  urlWithSize?: string;

  /**
   * Caption
   */
  caption?: string;
}

export interface ISalePrices {
  /**
   * Currency
   */
  currency?: ICurrency;

  /**
   * Discount in percentage
   */
  discount?: number;

  /**
   * Localised tooltip label for discount value
   */
  discountTooltip?: string;

  /**
   * Price to be paid initially when deposit booking is made
   */
  depositFromPrice?: IPrice;

  /**
   * The discounted rate offered by secret escapes
   */
  leadRate?: IPrice;

  /**
   * Localised label for discounted price
   */
  leadRateLabel?: string;

  /**
   * Localised tooltip label for discounted price
   */
  leadRateTooltip?: string;

  /**
   * Localised label for the discounted price unit
   */
  leadRateUnitLabel?: string;

  /**
   * The hotel rate, without any discounts
   */
  rackRate?: IPrice;

  /**
   * Max number of adults that can occupy same room
   */
  maxNumberOfAdults?: number;

  /**
   * Number of nights spent in hotel, without any nights spent in plane etc. Refers to one of offers, arbitrary chosen as best one for sale display purposes
   */
  numberOfHotelNights?: number;

  /**
   * Price of what is exposed to user. Eg per room, per person
   */
  pricingModelForDisplay?: string;

  /**
   * Display rules related to prices
   */
  pricingRules?: IPricingRules;
}

export interface ICurrency {
  /**
   * Currency code
   */
  currencyCode?: string;
}

export interface IPrice {
  /**
   * Pre-formatted price to be displayed verbatim in the display layer
   */
  forDisplay?: string;

  /**
   * Unit price
   */
  unit?: number;

  /**
   * Unit price per-person
   */
  unitPerPerson?: number;
}

export interface IPricingRules {
  /**
   * Whether the discount percentage should be shown or not
   */
  showDiscount?: boolean;

  /**
   * Whether the discounted prices should be shown or not
   */
  showPrices?: boolean;

  /**
   * Whether the original price should be shown or not
   */
  showRackRate?: boolean;
}

export interface ISaleAttributes {
  /**
   * Mysterious
   */
  isMysterious?: boolean;

  /**
   * Dynamic Package
   */
  isDynamicPackage?: boolean;

  /**
   * Smart Stay
   */
  isSmartStay?: boolean;

  /**
   * Editor's pick
   */
  isEditorsPick?: boolean;

  /**
   * Exclusive sale
   */
  isExclusive?: boolean;

  /**
   * Is deposit
   */
  isDepositSale?: boolean;

  /**
   * Is Zero deposit
   */
  isZeroDeposit?: boolean;

  /**
   * Whether it is current or upcoming sale
   */
  isCurrent?: boolean;

  /**
   * Is it connected sale
   */
  isConnected?: boolean;

  /**
   * Should timer be displayed
   */
  isTimeLimited?: boolean;
}

export interface ISaleLinks {
  /**
   * Relative link to sale details page
   */
  sale?: string;

  /**
   * Link to price comparsion
   */
  priceComparison?: string;

  /**
   * Link to tripadvisors page where hotel of the sale is reviewed
   */
  tripAdvisor?: string;
}

export interface ISalePromotion {
  /**
   * Label
   */
  label?: string;

  /**
   * Description
   */
  description?: string;

  /**
   * HexCode
   */
  hexCode?: string;

  /**
   * Tag name
   */
  tagName?: string;

  /**
   * Icon code
   */
  iconCode?: IIconName;
}

export enum IIconName {
  anchor = 'anchor',
  apple = 'apple',
  arrowdown = 'arrowdown',
  arrowright = 'arrowright',
  arrow = 'arrow',
  asterisk = 'asterisk',
  bell = 'bell',
  bolt = 'bolt',
  book = 'book',
  bookmark = 'bookmark',
  calendar = 'calendar',
  camera = 'camera',
  caret = 'caret',
  certificate = 'certificate',
  checkmark = 'checkmark',
  close = 'close',
  creditcard = 'creditcard',
  deposit = 'deposit',
  desktop = 'desktop',
  dollar = 'dollar',
  earth = 'earth',
  euro = 'euro',
  facebook = 'facebook',
  film = 'film',
  flag = 'flag',
  food = 'food',
  gift = 'gift',
  glass = 'glass',
  google = 'google',
  heart = 'heart',
  heartempty = 'heartempty',
  home = 'home',
  iconBed = 'iconBed',
  info = 'info',
  key = 'key',
  leaf = 'leaf',
  load = 'load',
  lock = 'lock',
  longarrowdown = 'longarrowdown',
  marker = 'marker',
  minus = 'minus',
  money = 'money',
  music = 'music',
  picture = 'picture',
  plane = 'plane',
  plus = 'plus',
  pound = 'pound',
  refresh = 'refresh',
  road = 'road',
  share = 'share',
  shield = 'shield',
  shoppingcart = 'shoppingcart',
  smile = 'smile',
  star = 'star',
  starempty = 'starempty',
  suitcase = 'suitcase',
  sun = 'sun',
  tag = 'tag',
  thumbsupalt = 'thumbsupalt',
  ticket = 'ticket',
  time = 'time',
  tint = 'tint',
  travel = 'travel',
  traveller = 'traveller',
  trophy = 'trophy',
  twitter = 'twitter',
  unlockalt = 'unlockalt',
  usd = 'usd',
  user = 'user',
  warningsign = 'warningsign',
  zoom = 'zoom',
}

export interface ISaleLabel {
  /**
   * HexCode
   */
  hexCode: string;

  /**
   * Icon code
   */
  iconCode: IIconName;

  /**
   * Label Key
   */
  labelKey?: string;

  /**
   * Summary
   */
  summary?: string;
}

export interface ISaleTravel {
  /**
   * is flight already included in price
   */
  hasFlightsIncluded?: boolean;

  /**
   * can the flight be added while booking is made
   */
  hasFlightsAvailable?: boolean;

  /**
   * Type of travel, eg flight, train
   */
  travelType?: string;
}

export interface ILocation {
  /**
   * City
   */
  city?: ILocationEntry;

  /**
   * Country
   */
  country?: ILocationEntry;

  /**
   * Continent
   */
  continent?: ILocationEntry;

  /**
   * Division
   */
  division?: ILocationEntry;

  /**
   * Latitude
   */
  latitude?: number;

  /**
   * Longitude
   */
  longitude?: number;
}

export interface ILocationEntry {
  id?: number;
  name?: string;
}

export interface ISaleEditorial {
  /**
   * Title
   */
  title?: string;

  /**
   * User friendly, one sentence destination usually including hotel name, city, region name
   */
  destinationName?: string;

  /**
   * Short, one sentence information about cool things about the venue
   */
  reasonToLove?: string;

  /**
   * Where user is going to sleep eg room, tent
   */
  roomDescription?: string;

  /**
   * Deal includes
   */
  dealIncludes?: string;

  /**
   * We Like
   */
  weLike?: string;

  /**
   * Main paragraph
   */
  mainParagraph?: string;

  /**
   * Hotel details
   */
  hotelDetails?: string;

  /**
   * How hotel is described at other web pages
   */
  secondOpinion?: string;

  /**
   * Travel details
   */
  travelDetails?: string;

  /**
   * summary
   */
  summary?: string;
}

export interface IBookingStats {
  /**
   * Times booked already
   */
  timesBooked?: number;

  /**
   * Visitors looking at the sale
   */
  currentVisitors?: number;
}

export interface IOffer {
  /**
   * ID of the offer
   */
  id: string;
  /**
   * The calendar for the offer
   */
  calendar?: ICalendar;
  /**
   * Summary of calendar selection for specific days
   */
  calendarSelectionSummary?: ICalendarSelectionSummary;
}

export interface ICalendar {
  /**
   * The type of stay
   */
  stayType: IStayType;
  /**
   * The currency for the displayed amounts
   */
  currency: ICurrency;
  /**
   * The available dates for booking
   */
  calendarEntries?: Array<ICalendarEntry | null>;
}

export enum IStayType {
  FLEXIBLE = 'FLEXIBLE',
  FIXED = 'FIXED',
}

export interface ICalendarEntry {
  /**
   * The date of stay
   */
  date: string;
  /**
   * The available prices
   */
  rates: ICalendarRates;
  /**
   * The minimum number of nights a user can select
   */
  minLengthOfStay: number;
  /**
   * The maximum number of nights a user can select
   */
  maxLengthOfStay: number;
  /**
   * Closed to arrival (CTA)
   */
  closedToArrival: boolean;
  /**
   * Closed to departure (CTD)
   */
  closedToDeparture: boolean;
  /**
   * Check out day only
   */
  checkOutDayOnly: boolean;
}

export interface ICalendarRates {
  rate: IPrice;
}

export interface ICalendarSelectionSummary {
  cancellationSelectionInformation?: string;
  resortFeeDisclaimer?: string;
  rateCodes?: string;
  priceBreakdown?: IPriceBreakdown;
  alreadyPaidBreakdown?: IPriceBreakdown;
  balance: IPrice;
}

export interface IPriceBreakdown {
  /**
   * Breakdown items
   */
  items?: Array<IPriceBreakdownItem | null>;
  /**
   * Total price of all items
   */
  total: IPrice;
}

export interface IPriceBreakdownItem {
  /**
   * Item concept
   */
  concept: string;
  /**
   * Is item Tax or Fee
   */
  isTaxOrFee?: boolean;
  /**
   * Item price
   */
  price: IPrice;
}

export interface IUpcomingSalesResults {
  /**
   * Count of results
   */
  resultCount?: number;

  /**
   * List of sales
   */
  sales?: Array<ISale | null>;
}

export interface ITerritoryConfiguration {
  /**
   * Dynamic config value to determine if ASA is enabled
   */
  isASAEnabled?: boolean;

  /**
   * Price threshold value determined by dynamic config
   */
  priceThreshold?: number;

  /**
   * Is flight functionality enabled
   */
  isFlightFunctionalityEnabled?: boolean;

  /**
   * Indicate if we can show the flights available label on the current sales page
   */
  flightsAvailableAtCSPage?: boolean;

  /**
   * Indicate if we can show the deposit label on the current sales page
   */
  depositAvailableAtCSPage?: boolean;

  /**
   * Indicate if we can show the deposit label on the current sales page
   */
  isZeroDepositDisplayable?: boolean;

  /**
   * Point of sale locale - related to territory of page the user is seeing, eg en_GB
   */
  posLocale?: string;

  /**
   * Locale of territory user is assigned to, eg de_DE
   */
  userLocale?: string;
}

export interface ISearchResults {
  /**
   * List of sales
   */
  sales?: Array<ISale | null>;

  /**
   * Search query
   */
  query?: string;

  /**
   * Spell checked suggestion
   */
  spellCheckedQuery?: string;

  /**
   * Refinement facets list
   */
  refinementFacets?: Array<IRefinementFacetsEntry | null>;

  /**
   * Count of results
   */
  resultCount?: number;
  /**
   * Other possible matching sales, eg non-exact matches,
   * recommendations, upcoming, past sales, etc
   */
  otherMatches?: Array<IOtherMatchesEntry | null>;
}

export interface IRefinementFacetsEntry {
  /**
   * Refinement facet entry name
   */
  name?: string;

  /**
   * List of elements
   */
  value?: Array<string | null>;
}

export interface IMutation {
  addFavorite: IFavoriteResponse;
  removeFavorite: IFavoriteResponse;
}

export interface IFavoriteResponse {
  success: boolean;
  message?: string;
}

export interface IOffer {
  maxAdults: number;
  maxDependants: number;
}

export interface IOtherMatchesEntry {
  /**
   * List of sales
   */
  sales: ISale[];
  /**
   * Key to identify the section type, to get the translastion the title of the
   * section, the key should be prefixed with 'search.othermatches.'
   */
  titleKey: string;
}

export interface ICollection {
  id?: number;
  title?: string;
  description?: string;
  url?: string;
  tags?: Array<string | null>;
  tileImage?: string;
  metaTitle?: string;
  metaKeywords?: string;
  metaDescription?: string;

  /**
   * Absolute URL for collection hero image
   */
  heroUrl?: string;

  /**
   * Absolute URL for collection hero image, with a template to replace with actual height and width
   */
  heroUrlWithSize?: string;
}
